import React, { useState, useEffect, useMemo, useCallback } from "react";
import Modal from "react-modal";
import {
  collection,
  onSnapshot,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  deleteDoc
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import Swal from "sweetalert2";
import FacturaDetallesModal from "./FacturaDetallesModal";

Modal.setAppElement("#root");

const fullScreenStyles = {
  content: {
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    padding: "20px",
    border: "none",
    borderRadius: "0",
    overflow: "auto",
    fontFamily: "Arial, sans-serif",
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 130,
  },
};

export default function InvoiceSearchModal({ isOpen, onClose, pedidoId }) {
  // Formatea un número al estilo español con separador de miles y 2 decimales
  const formatNumber = (value) =>
    Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // Estados de búsqueda y resultados
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCliente, setSearchCliente] = useState("");
  const [searchFolio, setSearchFolio] = useState("");
  const [searchFecha, setSearchFecha] = useState("");
  const [searchMonto, setSearchMonto] = useState("");
  const [facturasClientes, setFacturasClientes] = useState([]);
  const [facturasResults, setFacturasResults] = useState([]);
  const [selectedFacturas, setSelectedFacturas] = useState([]);
  const [associatedFacturas, setAssociatedFacturas] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFacturaDetail, setShowFacturaDetail] = useState(false);
  const [previewFactura, setPreviewFactura] = useState(null);
 
  // ↪ recarga la lista de facturas asociadas al pedido
  const reloadAssociatedFacturas = useCallback(async () => {
    const snap = await getDocs(
      collection(db, "pedidos", pedidoId, "facturas")
    );
    setAssociatedFacturas(
      snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(f => Number(f.monto) > 0)
    );
  }, [pedidoId]);

  // Carga real-time de todas las facturas emitidas
  useEffect(() => {
    if (!isOpen) return;
    const unsub = onSnapshot(
      collection(db, "facturasEmitidas"),
      (snap) =>
        setFacturasClientes(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, [isOpen]);

  // 2) Unicidad (igual que antes)
  const uniqueFacturas = useMemo(() => {
    const seen = new Set();
    return facturasClientes.filter((f) => {
      const key =
        f.UUID?.trim() ||
        `${f.Folio}-${f.receptor_Rfc}-${f.TipoDeComprobante}-${f.Fecha}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [facturasClientes]);

  useEffect(() => {
    if (!isOpen) return;
    reloadAssociatedFacturas();
  }, [isOpen, reloadAssociatedFacturas]);  

  // 4) Filtro según todos los campos
  const handleSearch = () => {
    let res = uniqueFacturas;
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      res = res.filter((f) => {
        const fechaStr = f.Fecha
          ? new Date(f.Fecha).toLocaleDateString("es-ES")
          : "";
        return [
          f.Folio?.toString(),
          f.receptor_Nombre,
          f.receptor_Rfc,
          f.TipoDeComprobante,
          f.Total?.toString(),
          fechaStr,
        ].some((campo) => campo?.toLowerCase().includes(term));
      });
    }
    if (searchCliente) {
      res = res.filter((f) =>
        f.receptor_Nombre
          .toLowerCase()
          .includes(searchCliente.toLowerCase())
      );
    }
    if (searchFolio) {
      res = res.filter((f) =>
        f.Folio.toString().includes(searchFolio)
      );
    }
    if (searchFecha) {
      const tgt = new Date(searchFecha).toLocaleDateString("es-ES");
      res = res.filter(
        (f) =>
          new Date(f.Fecha).toLocaleDateString("es-ES") === tgt
      );
    }
    if (searchMonto) {
      res = res.filter((f) => Number(f.Total) === Number(searchMonto));
    }
 
    // ── FILTRO ADICIONAL: sólo facturas con Total > 0 ──
    res = res.filter(f => Number(f.Total) > 0);
 
    setFacturasResults(res);
    setHasSearched(true);
  };

  // 5) Al hacer “Asociar”
  const handleAssociate = async () => {
    if (selectedFacturas.length === 0) {
      return Swal.fire("Selecciona al menos una factura", "", "warning");
    }
 
    const batchJobs = selectedFacturas.map(fid => {
      const fac = facturasClientes.find(fc => fc.id === fid);
      if (!fac) return null;        // seguridad
 
      return addDoc(collection(db, "pedidos", pedidoId, "facturas"), {
        idFactura: fac.id,
        folio: fac.Folio,
        cliente: fac.receptor_Nombre,
        rfc: fac.receptor_Rfc,
        tipoComprobante: fac.TipoDeComprobante,
        monto: fac.Total,
        estado: fac.estado || "Pendiente",
        fechaEmision: fac.Fecha,
        asociadoEn: serverTimestamp(),
      });
    });
 
    await Promise.all(batchJobs);
    Swal.fire("¡Hecho!", "Facturas asociadas correctamente", "success");
    await reloadAssociatedFacturas();
    setSelectedFacturas([]);        // limpia selección
  };  

  if (!isOpen) return null;

  // Elimina una factura asociada y recarga el listado
  const handleDeleteAssociated = async (assocId) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará la factura asociada.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
 
    if (result.isConfirmed) {
      await deleteDoc(doc(db, "pedidos", pedidoId, "facturas", assocId));
      await reloadAssociatedFacturas();
      Swal.fire("Eliminada", "La factura ha sido eliminada.", "success");
    }
  };  

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={fullScreenStyles}
      contentLabel="Asociar factura"
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h2 style={{ margin: 0 }}>Asociar factura</h2>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            fontSize: "1.5rem",
            cursor: "pointer",
          }}
        >
          &times;
        </button>
      </div>

      {/* Buscador unificado */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          style={{ flex: 1 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar factura..."
        />
        <button className="btn btn-secondary" onClick={handleSearch}>
          <i className="bi bi-search" />
        </button>
      </div>

      {/* Filtros avanzados */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <input
          value={searchCliente}
          onChange={(e) => setSearchCliente(e.target.value)}
          placeholder="Cliente"
        />
        <input
          value={searchFolio}
          onChange={(e) => setSearchFolio(e.target.value)}
          placeholder="Folio"
        />
        <input
          type="date"
          value={searchFecha}
          onChange={(e) => setSearchFecha(e.target.value)}
        />
        <input
          type="number"
          value={searchMonto}
          onChange={(e) => setSearchMonto(e.target.value)}
          placeholder="Monto"
        />
      </div>

      {/* Resultados de la búsqueda */}
      {hasSearched && (
        facturasResults.length > 0 ? (
          <div
            style={{
              maxHeight: "80vh",
              overflowY: "auto",
              marginBottom: 12,
            }}
          >
            <table className="table table-hover">
              <thead>
                <tr>
                  <th></th>
                  <th>Folio</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th className="text-end">Monto</th>
                </tr>
              </thead>
              <tbody>
                {facturasResults.map((f) => (
                  <tr
                    key={f.id}
                    onClick={() => {
                       setPreviewFactura(f);          /* abre vista previa */
                       setShowFacturaDetail(true);
                    }}
                    className={previewFactura?.id === f.id ? "table-active" : ""}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={selectedFacturas.includes(f.id)}
                        onClick={e => e.stopPropagation()}          // no abre modal
                        onChange={() =>                       // marca / des-marca
                          setSelectedFacturas(prev =>
                            prev.includes(f.id)
                              ? prev.filter(id => id !== f.id)
                              : [...prev, f.id]
                          )
                        }
                      />
                    </td>
                    <td>{f.Folio}</td>
                    <td>{f.receptor_Nombre}</td>
                    <td>
                      {new Date(f.Fecha).toLocaleDateString("es-ES")}
                    </td>
                    <td className="text-end">
                      {formatNumber(f.Total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ marginBottom: 12 }}>No se encontraron facturas.</p>
        )
      )}

      {/* Facturas ya asociadas */}
      {associatedFacturas.length > 0 && (
        <>
            <h5 style={{ fontSize: 16 }}>Búsquedas asociadas:</h5>
            <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 12 }}>
            <table className="table table-sm">
                <thead>
                <tr>
                    <th>Folio</th>
                    <th>Cliente</th>
                    <th className="text-end">Monto</th>
                    <th>Fecha asoc.</th>
                    <th className="w-8 text-center">Acciones</th>
                </tr>
                </thead>
                <tbody>
                {associatedFacturas.map((f) => (
                    <tr
                      key={f.id}
                      onClick={() => {
                        setPreviewFactura(f);            // solo pre-visualización
                        setShowFacturaDetail(true);
                      }}
                      className={previewFactura?.id === f.id ? "table-active" : ""}
                      style={{ cursor: "pointer" }}
                    >                  
                    <td>{f.folio}</td>
                    <td>{f.cliente || f.razonSocial || "–"}</td>
                    <td className="text-end">
                      {"$" + formatNumber(f.monto)}
                    </td>
                    <td>
                        {f.asociadoEn
                        ? new Date(f.asociadoEn.seconds * 1000).toLocaleDateString()
                        : ""}
                    </td>
                    <td>
                        <button
                          className="p-0 bg-transparent hover:bg-transparent text-red-500 hover:text-red-700"
                          onClick={(e) => {
                              e.stopPropagation();        // ← evita que suba al <tr>
                              handleDeleteAssociated(f.id);
                          }}
                        >
                        <i className="bi bi-trash text-lg"></i>
                        </button>
                    </td>
                    </tr>
                ))}
                </tbody>
            </table>
            </div>
        </>
        )}

        <FacturaDetallesModal
          isOpen={showFacturaDetail}
          onRequestClose={() => setShowFacturaDetail(false)}
          factura={previewFactura}
          onlyDetails={true}
        />

      {/* Footer */}
      <div style={{ textAlign: "right" }}>
        <button
          onClick={onClose}
          className="btn btn-outline-secondary me-2"
        >
          Cancelar
        </button>
        <button
          onClick={handleAssociate}
          className="btn btn-primary"
        >
          Asociar
        </button>
      </div>
    </Modal>
  );
}
